"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useClickOutsideHandler } from '@/hooks/useClickOutsideHandler';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, CreditCard } from '@/components/icons';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useScrollLock } from '@/hooks/useScrollLock';
import BankAccountsManager from './BankAccountsManager';

interface BankAccountsSidebarProps {
  entityId: string; // storeId or userId
  mode: 'store' | 'user';
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  title?: string;
}

export const BankAccountsSidebar: React.FC<BankAccountsSidebarProps> = ({ 
  entityId, 
  mode, 
  isOpen, 
  onClose, 
  onSaved,
  title = 'Cuentas Bancarias'
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { headerHeight, isDesktop } = useHeaderOffset();

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  }, [onClose]);

  useScrollLock(isOpen || isClosing, false, scrollAreaRef);

  // Click fuera para cerrar
  // Click fuera para cerrar - usando hook reutilizable
  useClickOutsideHandler({
    isOpen,
    sidebarRef,
    handleClose,
  });

  // Cerrar con tecla ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  return (
    <AnimatePresence>
      {(isOpen || isClosing) && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-transparent z-[9998]"
            onClick={handleClose}
          />

          {/* Sidebar */}
          <motion.div
            ref={sidebarRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.2 }}
            className={cn(
              'fixed right-0 w-full lg:w-[400px] bg-background shadow-2xl z-[9999] flex flex-col'
            )}
            style={{
              top: isDesktop ? `${headerHeight}px` : '0',
              bottom: !isDesktop ? 0 : undefined,
              height: isDesktop ? `calc(100vh - ${headerHeight}px)` : '100dvh',
              pointerEvents: (isOpen && !isClosing) ? 'auto' : 'none'
            }}
          >
            {/* Safe Area Spacer for Mobile */}
            {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                {title}
              </h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleClose(); }}
                      className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent variant="sidebar">
                    <p>Cerrar</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Content */}
            <ScrollArea ref={scrollAreaRef} className="flex-1">
              <div className="p-4">
                <BankAccountsManager 
                  entityId={entityId} 
                  mode={mode}
                  onSaved={onSaved}
                />
              </div>
            </ScrollArea>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
